import { FC, useState, useContext } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import { useFragment, graphql, useRelayEnvironment } from 'react-relay';
import { setBuyerChargeCurrency } from '../../actions/checkoutUIActions';
import { trackFormInteraction } from '../../helpers/checkoutTracking';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import styles from './styles/CheckoutBuyerChargeCurrency.scss';
import { CheckoutServerVarsContext } from './CheckoutServerVarsContext';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { useAppDispatch, useCheckoutSelector } from '../../reducers/root';

import { BasicSelect as Select } from 'dibs-elements/exports/BasicSelect';
import { ExpandingArea } from 'dibs-elements/exports/ExpandingArea';

import { CheckoutBuyerChargeCurrency_viewer$key } from './__generated__/CheckoutBuyerChargeCurrency_viewer.graphql';
import { CheckoutBuyerChargeCurrency_transaction$key } from './__generated__/CheckoutBuyerChargeCurrency_transaction.graphql';

const messages = defineMessages({
    label: {
        id: 'checkout.CheckoutBuyerChargeCurrency.header',
        defaultMessage: 'Pay In Another Currency',
    },
    placeholder: {
        id: 'checkout.CheckoutBuyerChargeCurrency.placeholder',
        defaultMessage: 'Select supported currency',
    },
});

type Currency = 'USD' | 'GBP' | 'EUR';

type Props = {
    transaction: CheckoutBuyerChargeCurrency_transaction$key;
    viewer: CheckoutBuyerChargeCurrency_viewer$key;
};

const CheckoutBuyerChargeCurrency: FC<Props> = ({
    transaction: transactionRef,
    viewer: viewerRef,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const intl = useIntl();
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const environment = useRelayEnvironment();
    const canSelectChargeCurrency = useCheckoutSelector(state => state.ui.canSelectChargeCurrency);
    const buyerChargeCurrency = useCheckoutSelector(state => state.transaction.buyerChargeCurrency);
    const dispatch = useAppDispatch();

    const viewer = useFragment(
        graphql`
            fragment CheckoutBuyerChargeCurrency_viewer on Viewer {
                chargeCurrencies
            }
        `,
        viewerRef
    );
    const transaction = useFragment(
        graphql`
            fragment CheckoutBuyerChargeCurrency_transaction on Transaction {
                serviceId
            }
        `,
        transactionRef
    );

    const shouldShowCurrencySelector = canSelectChargeCurrency && transaction?.serviceId;
    if (!shouldShowCurrencySelector) {
        return null;
    }

    const chargeCurrencies = viewer?.chargeCurrencies || [];

    const onToggle = function (): void {
        if (!isOpen) {
            trackFormInteraction('buyer charge currency expanded');
        }
        setIsOpen(!isOpen);
    };

    const onSelect = async function (selectedCurrency: Currency): Promise<void> {
        dispatch(setBuyerChargeCurrency(selectedCurrency));
        try {
            await CheckoutMutationWrapper.commit(
                'TransactionCheckoutChangeBuyerChargeCurrencyMutation',
                environment,
                {
                    transactionId: transaction?.serviceId || '',
                    buyerChargeCurrency: selectedCurrency,
                },
                isMobile
            );
        } catch (error) {
            logCheckoutError(error, 'chargeCurrency');
        }
    };

    const labelMessage = intl.formatMessage(messages.label);

    return (
        canSelectChargeCurrency &&
        (isMobile ? (
            <div className={styles.positionContainer}>
                <span className={styles.chargeCurrencyLink}>{labelMessage}</span>
                <select
                    name="charge-currency"
                    data-tn="charge-currency"
                    value={buyerChargeCurrency}
                    onChange={event => {
                        onSelect(event.target.value as Currency);
                    }}
                    className={styles.positionChild}
                >
                    {chargeCurrencies.map(currency => {
                        return (
                            <option value={currency || ''} key={currency}>
                                {currency}
                            </option>
                        );
                    })}
                </select>
            </div>
        ) : (
            <ExpandingArea
                label={labelMessage}
                showArrow
                expanded={isOpen}
                wrapperClass={styles.wrapper}
                expandingAreaClass={styles.expandingArea}
                hideCollapsedContent
                dataTn="buyer-charge-currency"
                onClick={onToggle}
            >
                {isOpen && (
                    <Select
                        size="medium"
                        name="buyer-charge-currency"
                        value={buyerChargeCurrency}
                        placeholder={intl.formatMessage(messages.placeholder)}
                        options={chargeCurrencies.map(currency => ({
                            value: currency || '',
                            label: currency || '',
                        }))}
                        dataTn="buyer-charge-currency-dropdown"
                        onChange={event => {
                            onSelect(event.target.value as Currency);
                        }}
                    />
                )}
            </ExpandingArea>
        ))
    );
};

export default CheckoutBuyerChargeCurrency;
